// AdminHsDisagreement.jsx — 세번 불일치 판정(2026-10-10, 학습 순환 1·2단계)
// ──────────────────────────────────────────────────────
// 신고 세번과 PROVE 모델 세번이 다른 건을 모아 둔 원장. 차이 판정 후보(가르는 사실·물품 문장·해설서 문장)를 보고
// 관리자가 한 번 눌러 확정하면 그것만 다음 학습의 정답이 된다. 봉인(시험용) 건은 표시만 하고 똑같이 확정한다.
//   GET  /api/onto/admin/hs-disagreement?state=open|done|all
//   POST /api/onto/admin/hs-disagreement/{id}/verdict {verdict, verdict_hsk?, note?}
//   GET  /api/onto/admin/hs-disagreement/export?holdout=0|1  확정분 JSONL
// ──────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from "react";

function authHeaders(json = false) {
  const token = localStorage.getItem("admin_token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { "Content-Type": "application/json" } : {}) };
}

export const fmtHsk = (h) => {
  const d = String(h || "").replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}` : d;
};

const BLUE = "#2563eb";
const MUTED = "#6b7280";
const LINE = "#e5e7eb";
const S = {
  wrap: { padding: 20, color: "#1f2937", fontSize: 13 },
  card: { border: `1px solid ${LINE}`, borderRadius: 10, padding: 14, marginBottom: 12, background: "#fff" },
  btn: { border: `1px solid ${LINE}`, background: "#fff", borderRadius: 6, padding: "5px 12px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  primary: { border: `1px solid ${BLUE}`, background: BLUE, color: "#fff", borderRadius: 6, padding: "5px 12px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  code: { fontFamily: "Consolas, monospace", fontWeight: 700 },
  quote: { borderLeft: `3px solid ${LINE}`, padding: "2px 8px", color: "#374151", margin: "4px 0" },
};
const STATES = [["open", "판정 대기"], ["done", "확정"], ["all", "전체"]];
const REC = { declared: "신고 세번 쪽", model: "모델 세번 쪽", unsure: "판단 보류" };
const VERDICT = { declared: "신고가 맞음", model: "모델이 맞음", neither: "둘 다 아님", unsure: "보류" };
// 확정 근거(2026-10-10 사용자: 무역거래처에 메일 등으로 제품을 확인했으면 그 근거를 붙여 확정)
export const EVIDENCE_KINDS = [["partner_mail", "거래처 메일"], ["catalog", "카탈로그"], ["maker_site", "제조사 누리집"],
  ["site_check", "현장 확인"], ["precedent", "결정례"], ["other", "기타"]];
const EVIDENCE_LABEL = Object.fromEntries(EVIDENCE_KINDS);

export default function AdminHsDisagreement({ API_BASE_URL = "", fetchImpl = fetch }) {
  const [state, setState] = useState("open");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [neither, setNeither] = useState({});   // id → 입력한 올바른 세번
  const [notes, setNotes] = useState({});
  const [confirming, setConfirming] = useState({});   // id → {verdict, kind, text, url}

  const load = useCallback(async () => {
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-disagreement?state=${state}&limit=100`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError("관리자 로그인이 필요합니다(토큰 만료일 수 있습니다)"); return; }
      const j = await r.json();
      if (!r.ok) { setError(j.error || `오류 ${r.status}`); return; }
      setData(j); setError(null);
    } catch (e) { setError(String(e?.message || e)); }
  }, [API_BASE_URL, fetchImpl, state]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, verdict) => {
    setBusy(id);
    try {
      const body = { verdict, note: notes[id] || "" };
      if (verdict === "neither") body.verdict_hsk = neither[id] || "";
      const c = confirming[id];
      if (verdict !== "unsure" && c) body.evidence = { kind: c.kind, text: c.text, url: c.url || "" };
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-disagreement/${id}/verdict`,
        { method: "POST", headers: authHeaders(true), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || `오류 ${r.status}`); return; }
      setError(null);
      setConfirming((m) => { const n = { ...m }; delete n[id]; return n; });
      await load();
    } finally { setBusy(null); }
  };

  const download = async (holdout) => {
    const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-disagreement/export?holdout=${holdout}`, { headers: authHeaders() });
    const text = await r.text();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "application/x-ndjson" }));
    a.download = `hs-disagreement-${holdout ? "holdout" : "train"}.jsonl`;
    a.click();
  };

  const st = data?.stats;
  return (
    <div style={S.wrap} data-testid="admin-hs-disagreement">
      <h2 style={{ fontSize: 18, margin: "0 0 4px" }}>세번 불일치 판정</h2>
      <div style={{ color: MUTED, marginBottom: 12 }}>
        신고 세번과 PROVE 모델 세번이 다른 건입니다. 판정 후보(가르는 사실·근거 문장)를 보고 확정한 것만 다음 학습의 정답이 됩니다.
        세번을 자동으로 정하지 않습니다.
      </div>
      {st && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }} data-testid="hs-dis-stats">
          <span>전체 <b>{st.total}</b></span>
          <span>판정 대기 <b>{st.open}</b>{st.judging ? ` (근거 찾는 중 ${st.judging})` : ""}</span>
          <span>신고가 맞음 <b>{st.declared}</b></span>
          <span>모델이 맞음 <b>{st.model}</b></span>
          <span>둘 다 아님 <b>{st.neither}</b></span>
          <span style={{ color: MUTED }}>같은 수입자 꾸준 신고(약한 정답) {st.weak_open}</span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button type="button" style={S.btn} onClick={() => download(0)}>학습용 내보내기</button>
            <button type="button" style={S.btn} onClick={() => download(1)}>봉인(시험용) 내보내기</button>
          </span>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {STATES.map(([k, label]) => (
          <button key={k} type="button" style={state === k ? S.primary : S.btn} onClick={() => setState(k)} aria-pressed={state === k}>{label}</button>
        ))}
      </div>
      {error && <div role="alert" style={{ marginBottom: 12 }}>{error}</div>}
      {data && data.items.length === 0 && <div style={{ color: MUTED }}>건이 없습니다.</div>}
      {data && data.items.map((r) => {
        const j = r.judge || {};
        return (
          <div key={r.id} style={S.card} data-testid="hs-dis-item">
            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <span style={{ color: MUTED }}>#{r.id} · {r.source === "verify" ? "사후·사전검증" : r.source === "loginhs" ? "loginHS" : "문의"}{r.office ? ` · 사무소 ${r.office}` : ""}</span>
              {r.holdout && <span style={{ color: MUTED, border: `1px solid ${LINE}`, borderRadius: 4, padding: "0 5px", fontSize: 11 }}>봉인(시험용)</span>}
              {r.partner && <span style={{ color: MUTED }}>무역거래처 {r.partner}</span>}
              {r.verdict && <span style={{ color: BLUE, fontWeight: 600 }}>확정: {VERDICT[r.verdict]}{r.verdict_hsk ? ` ${fmtHsk(r.verdict_hsk)}` : ""}</span>}
            </div>
            <div style={{ margin: "6px 0", fontWeight: 600 }}>{r.text}</div>
            <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 3 }}>
              <span style={{ color: MUTED }}>신고 세번</span>
              <span><span style={S.code}>{fmtHsk(r.declared_hsk)}</span> {j.declared_name ? <span style={{ color: MUTED }}>{j.declared_name}</span> : null}</span>
              <span style={{ color: MUTED }}>모델 세번</span>
              <span><span style={S.code}>{fmtHsk(r.model_hsk)}</span> {j.model_name ? <span style={{ color: MUTED }}>{j.model_name}</span> : null}</span>
              {r.weak_label && (
                <>
                  <span style={{ color: MUTED }}>같은 수입자</span>
                  <span>신고 세번으로 {r.weak_detail?.top_count}회/{r.weak_detail?.total_count}회 신고(마지막 {r.weak_detail?.last_date || "—"}) · 약한 정답</span>
                </>
              )}
            </div>
            <div style={{ marginTop: 8 }} data-testid="hs-dis-judge">
              {r.judge_status !== "done" ? <span style={{ color: MUTED }}>근거를 찾는 중입니다(1분 안팎).</span> : (
                <>
                  <div><span style={{ color: MUTED }}>가르는 사실 </span>{j.deciding_question || "—"}</div>
                  {j.item_quote && <div style={S.quote}>물품: “{j.item_quote}”</div>}
                  {j.rule_quote && <div style={S.quote}>기준: “{j.rule_quote}”</div>}
                  <div><span style={{ color: MUTED }}>후보 </span><b style={{ color: j.recommendation === "unsure" ? MUTED : BLUE }}>{REC[j.recommendation] || "—"}</b>
                    {j.reason ? <span style={{ color: MUTED }}> · {j.reason}</span> : null}
                    {j.downgraded ? <span style={{ color: MUTED }}> · 인용이 원문에 없어 보류로 낮춤</span> : null}</div>
                </>
              )}
            </div>
            {(!r.verdict || r.verdict === "unsure") && confirming[r.id] && (
              <EvidenceForm value={confirming[r.id]} busy={busy === r.id}
                onChange={(v) => setConfirming({ ...confirming, [r.id]: v })}
                onCancel={() => setConfirming((m) => { const n = { ...m }; delete n[r.id]; return n; })}
                onSubmit={() => decide(r.id, confirming[r.id].verdict)} />
            )}
            {r.verdict_evidence && (
              <div style={{ marginTop: 8 }} data-testid="hs-dis-evidence">
                <span style={{ color: MUTED }}>확정 근거 · {EVIDENCE_LABEL[r.verdict_evidence.kind] || r.verdict_evidence.kind} </span>
                <div style={S.quote}>“{r.verdict_evidence.text}”</div>
                {r.verdict_evidence.url && <a href={r.verdict_evidence.url} target="_blank" rel="noreferrer" style={{ color: BLUE, fontSize: 12 }}>출처 열기 ↗</a>}
              </div>
            )}
            {(!r.verdict || r.verdict === "unsure") && !confirming[r.id] ? (
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button type="button" style={S.btn} disabled={busy === r.id} onClick={() => setConfirming({ ...confirming, [r.id]: { verdict: "declared", kind: "partner_mail", text: "", url: "" } })}>신고가 맞음</button>
                <button type="button" style={S.btn} disabled={busy === r.id} onClick={() => setConfirming({ ...confirming, [r.id]: { verdict: "model", kind: "partner_mail", text: "", url: "" } })}>모델이 맞음</button>
                <input value={neither[r.id] || ""} onChange={(e) => setNeither({ ...neither, [r.id]: e.target.value })}
                  placeholder="올바른 세번 10자리" aria-label="올바른 세번" style={{ border: `1px solid ${LINE}`, borderRadius: 6, padding: "4px 8px", width: 150, fontFamily: "Consolas, monospace" }} />
                <button type="button" style={S.btn} disabled={busy === r.id || String(neither[r.id] || "").replace(/\D/g, "").length !== 10} onClick={() => setConfirming({ ...confirming, [r.id]: { verdict: "neither", kind: "partner_mail", text: "", url: "" } })}>둘 다 아님</button>
                <button type="button" style={{ ...S.btn, color: MUTED }} disabled={busy === r.id} onClick={() => decide(r.id, "unsure")}>보류</button>
                <input value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                  placeholder="메모(선택)" aria-label="판정 메모" style={{ border: `1px solid ${LINE}`, borderRadius: 6, padding: "4px 8px", flex: "1 1 160px" }} />
              </div>
            ) : (r.verdict_note && r.verdict !== "unsure" ? <div style={{ color: MUTED, marginTop: 6 }}>메모: {r.verdict_note}</div> : null)}
          </div>
        );
      })}
    </div>
  );
}

/** 확정 근거 입력 — 근거 종류·확인한 제품 설명(필수)·출처 주소(선택). 학습 내보내기에 '확인한 제품 설명'으로 함께 간다. */
function EvidenceForm({ value, busy, onChange, onCancel, onSubmit }) {
  const ok = String(value.text || "").trim().length >= 5;
  const field = { border: `1px solid ${LINE}`, borderRadius: 6, padding: "5px 8px", font: "inherit", fontSize: 13 };
  return (
    <div data-testid="hs-dis-evidence-form" style={{ marginTop: 10, border: `1px solid ${LINE}`, borderRadius: 8, padding: 10, display: "grid", gap: 6 }}>
      <div><b>{VERDICT[value.verdict]}</b> <span style={{ color: MUTED }}>— 근거를 붙여 확정합니다. 이 설명이 학습 자료로 함께 갑니다.</span></div>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ color: MUTED, width: 90 }}>근거 종류</span>
        <select value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value })} aria-label="근거 종류" style={field}>
          {EVIDENCE_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ color: MUTED }}>확인한 제품 설명 (메일·카탈로그에서 옮긴 문장)</span>
        <textarea value={value.text} onChange={(e) => onChange({ ...value, text: e.target.value })} rows={3} maxLength={2000}
          aria-label="확인한 제품 설명" placeholder="예: 덕트에 고정 설치하는 스파크 감지·자동 물 분사 설비로, 감지기·제어반·노즐로 구성" style={field} />
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ color: MUTED, width: 90 }}>출처 주소</span>
        <input value={value.url} onChange={(e) => onChange({ ...value, url: e.target.value })} placeholder="선택 · https://…" aria-label="출처 주소" style={{ ...field, flex: 1 }} />
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" style={ok && !busy ? S.primary : S.btn} disabled={!ok || busy} onClick={onSubmit} data-testid="hs-dis-confirm">근거 붙여 확정</button>
        <button type="button" style={S.btn} onClick={onCancel}>취소</button>
      </div>
    </div>
  );
}
